import {
  ArrowDownUpIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ListFilterIcon,
  XIcon,
  type LucideIcon,
} from "lucide-react"
import { useState, type ReactNode } from "react"
import { Button } from "@/primitives/button.tsx"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/primitives/drawer.tsx"
import { Switch } from "@/primitives/switch.tsx"
import { cn } from "@/primitives/utils"
import type { DisplayRow, Facet, FacetOption } from "./filter-bar.tsx"
import type { OrderBy, ToolbarDisplay, ToolbarToggle, ViewChip } from "./page-toolbar.tsx"
import { chosenSummaryOf, parentOf, type ViewLevel } from "./page-toolbar.ts"
import { isSearchable, matching } from "./property.ts"
import { SearchRow } from "./property.tsx"
import { useViewport } from "./viewport.tsx"

/*
 * The compact PageToolbar's sheet, after Linear's filter and display sheets on a phone: one bottom
 * sheet that drills in level by level rather than stacking a second sheet. Its first level, View,
 * holds the Filter (a row that drills into the facets, and each facet into its values, with the view
 * choices that narrow the list under them), then the Order by and the Display choices, the Display
 * switches ruled off below them, the toggles that act on the view at once (Collapse all, Fold all),
 * and the count under its title. The toolbar's Filter button opens it straight at the facets.
 *
 * Each level is a 48px bar, Back (named for where it goes) at its start, the level's title and the
 * count in the middle, and Done at its end, over 44px rows: a drill-in row leads with its icon, in a
 * slot every drill-in row keeps so their names line up, says what is chosen and ends in a chevron,
 * and a chosen value is marked by its check alone. The Filter row says what is in force as the
 * Filter button counts it ("Priority: High", "2 filters"). A single choice goes back a level as it
 * is made; several stay open for the next. Nothing here owns the view: the page keeps it, as the
 * desk's controls leave it to the page.
 */

/** Which of the sheet's levels open, and where it opened from, which decides where Back goes. */
export type ViewSheetAt = { opened: "view" | "filter"; level: ViewLevel }

export function ViewSheet({
  open,
  at,
  onOpenChange,
  facets,
  chips,
  onClearFilters,
  inForce,
  filterSummary,
  orderBy,
  display,
  toggles,
  count,
}: {
  open: boolean
  at: ViewSheetAt
  onOpenChange: (open: boolean) => void
  /** The facets the Filter offers. */
  facets: readonly Facet[]
  /** The view choices in force that narrow the list, beside the facets ("Closed included"). */
  chips: readonly ViewChip[]
  onClearFilters: (() => void) | undefined
  /** How many filters are in force, which Clear filters takes back. */
  inForce: number
  /** What the Filter row says is in force (`filterSummaryOf`). */
  filterSummary: string
  orderBy: OrderBy | undefined
  display: ToolbarDisplay | undefined
  toggles: readonly ToolbarToggle[]
  count: string | undefined
}) {
  const [level, setLevel] = useState(at.level)
  // Each opening starts where the toolbar asked, not where the last one was left.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setLevel(at.level)
  }

  const parent = parentOf(level, at.opened)
  const facet = level.at === "facet" ? facets.find(({ id }) => id === level.id) : undefined
  const choice =
    level.at === "choice"
      ? display?.rows.find((row) => row.label === level.label && row.control.kind === "select")
      : undefined
  const back = () => parent && setLevel(parent)

  return (
    <Drawer open={open} onOpenChange={(next) => onOpenChange(next)}>
      <DrawerContent>
        <div className="sticky top-0 z-10 flex h-12 shrink-0 items-center gap-2 bg-popover px-2">
          <div className="flex flex-1 basis-0 justify-start">
            {parent && (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Back to ${titleOf(parent, { facets, orderBy, label: undefined })}`}
                onClick={back}
                className="-ml-1 pl-1"
              >
                <ChevronLeftIcon data-icon="inline-start" />
                {titleOf(parent, { facets, orderBy, label: undefined })}
              </Button>
            )}
          </div>
          <div className="min-w-0 text-center">
            <DrawerTitle className="truncate">
              {titleOf(level, { facets, orderBy, label: choice?.label })}
            </DrawerTitle>
            {count !== undefined && (
              <DrawerDescription className="text-xs tabular-nums">{count}</DrawerDescription>
            )}
          </div>
          <div className="flex flex-1 basis-0 justify-end">
            <DrawerClose render={<Button variant="ghost" size="sm" className="font-semibold" />}>
              Done
            </DrawerClose>
          </div>
        </div>
        <div className="pb-2">
          {level.at === "view" && (
            <ViewLevelRows
              facets={facets}
              filterSummary={filterSummary}
              orderBy={orderBy}
              display={display}
              toggles={toggles}
              onDrill={setLevel}
            />
          )}
          {level.at === "filter" && (
            <FacetRows
              facets={facets}
              chips={chips}
              inForce={inForce}
              onClearFilters={onClearFilters}
              onDrill={(id) => setLevel({ at: "facet", id })}
            />
          )}
          {facet && <FacetValueRows key={facet.id} facet={facet} onChosen={back} />}
          {level.at === "order" && orderBy && (
            <ChoiceRows
              label={orderBy.label ?? "Order by"}
              value={orderBy.value}
              options={orderBy.options}
              onChoose={(value) => {
                orderBy.onChange(value)
                back()
              }}
            />
          )}
          {choice?.control.kind === "select" && (
            <ChoiceRows
              label={choice.label}
              value={choice.control.value}
              options={choice.control.options}
              onChoose={(value) => {
                if (choice.control.kind === "select") choice.control.onChange(value)
                back()
              }}
            />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}

/** What a level is called, in its bar and on the Back that returns to it. */
function titleOf(
  level: ViewLevel,
  {
    facets,
    orderBy,
    label,
  }: { facets: readonly Facet[]; orderBy: OrderBy | undefined; label: string | undefined },
) {
  switch (level.at) {
    case "view":
      return "View"
    case "filter":
      return "Filter"
    case "facet":
      return facets.find(({ id }) => id === level.id)?.label ?? "Filter"
    case "order":
      return orderBy?.label ?? "Order by"
    case "choice":
      return label ?? level.label
  }
}

// A row of the sheet: 44px, edge to edge, its press filled as the popover's highlight.
const rowClass =
  "flex min-h-11 w-full items-center gap-3 px-4 text-left text-ui outline-none select-none transition-colors duration-100 ease-snappy hover:bg-popover-accent focus-visible:bg-popover-accent"

/** One group of rows, ruled off from the group before it. */
function Group({ first, children }: { first: boolean; children: ReactNode }) {
  return (
    <div role="group" className={cn("py-1", !first && "border-t border-border-subtle")}>
      {children}
    </div>
  )
}

/**
 * A row that drills in: its icon, in a slot kept when it has none so every name lines up, its name,
 * what is chosen there, and a chevron.
 */
function DrillRow({
  icon: Icon,
  label,
  value,
  onClick,
}: {
  icon?: LucideIcon
  label: string
  value: string | undefined
  onClick: () => void
}) {
  return (
    <button type="button" className={rowClass} onClick={onClick}>
      <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
        {Icon && <Icon className="size-4 text-muted-foreground" />}
      </span>
      <span className="shrink-0">{label}</span>
      <span className="ml-auto min-w-0 truncate text-muted-foreground">{value}</span>
      <ChevronRightIcon aria-hidden className="size-4 shrink-0 text-subtle-foreground" />
    </button>
  )
}

/**
 * The View's first level: the Filter; the Order by and the Display choices; the Display switches;
 * then the toggles. The switches, which lead with no icon, are ruled off from the drill-in rows.
 */
function ViewLevelRows({
  facets,
  filterSummary,
  orderBy,
  display,
  toggles,
  onDrill,
}: {
  facets: readonly Facet[]
  filterSummary: string
  orderBy: OrderBy | undefined
  display: ToolbarDisplay | undefined
  toggles: readonly ToolbarToggle[]
  onDrill: (level: ViewLevel) => void
}) {
  const groups: ReactNode[] = []
  if (facets.length > 0)
    groups.push(
      <DrillRow
        icon={ListFilterIcon}
        label="Filter"
        value={filterSummary}
        onClick={() => onDrill({ at: "filter" })}
      />,
    )
  const rows = display?.rows ?? []
  if (orderBy || rows.some(({ control }) => control.kind === "select"))
    groups.push(
      <>
        {orderBy && (
          <DrillRow
            icon={ArrowDownUpIcon}
            label={orderBy.label ?? "Order by"}
            value={orderBy.options.find(({ value }) => value === orderBy.value)?.label}
            onClick={() => onDrill({ at: "order" })}
          />
        )}
        {rows.map(
          ({ label, icon, control }) =>
            control.kind === "select" && (
              <DrillRow
                key={label}
                icon={icon}
                label={label}
                value={control.options.find(({ value }) => value === control.value)?.label}
                onClick={() => onDrill({ at: "choice", label })}
              />
            ),
        )}
      </>,
    )
  if (rows.some(({ control }) => control.kind === "switch"))
    groups.push(
      rows.map(
        ({ label, control }) =>
          control.kind === "switch" && <SwitchRow key={label} label={label} control={control} />,
      ),
    )
  if (toggles.length > 0)
    groups.push(
      toggles.map(({ label, icon: Icon, onSelect }) => (
        <button key={label} type="button" className={rowClass} onClick={onSelect}>
          <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          {label}
        </button>
      )),
    )
  return groups.map((group, index) => (
    <Group key={index} first={index === 0}>
      {group}
    </Group>
  ))
}

/**
 * A Display option that is on or off: a switch, which an option that doesn't apply to the view
 * shows faded, its reason in a line under it.
 */
function SwitchRow({
  label,
  control,
}: {
  label: string
  control: Extract<DisplayRow["control"], { kind: "switch" }>
}) {
  const disabled = control.disabledReason !== undefined
  return (
    <div>
      <label className={cn(rowClass, "hover:bg-transparent", disabled && "opacity-50")}>
        <span className="min-w-0 flex-1">{label}</span>
        <Switch
          checked={control.checked}
          disabled={disabled}
          onCheckedChange={(checked) => control.onChange(checked)}
        />
      </label>
      {control.disabledReason && (
        <p className="-mt-1.5 px-4 pb-2 text-xs text-muted-foreground">{control.disabledReason}</p>
      )}
    </div>
  )
}

/**
 * The facets the Filter offers, each saying what is chosen in it; the view choices in force that
 * narrow the list, each taken back by a tap, as its chip's × does; then Clear filters.
 */
function FacetRows({
  facets,
  chips,
  inForce,
  onClearFilters,
  onDrill,
}: {
  facets: readonly Facet[]
  chips: readonly ViewChip[]
  inForce: number
  onClearFilters: (() => void) | undefined
  onDrill: (id: string) => void
}) {
  return (
    <>
      <Group first>
        {facets.map((facet) => (
          <DrillRow
            key={facet.id}
            icon={facet.icon}
            label={facet.label}
            value={chosenSummaryOf(facet)}
            onClick={() => onDrill(facet.id)}
          />
        ))}
      </Group>
      {chips.length > 0 && (
        <Group first={false}>
          {chips.map(({ label, onRemove }) => (
            <button
              key={label}
              type="button"
              aria-label={`Remove ${label}`}
              className={rowClass}
              onClick={onRemove}
            >
              <span className="min-w-0 flex-1 truncate">{label}</span>
              <XIcon aria-hidden className="size-4 shrink-0 text-subtle-foreground" />
            </button>
          ))}
        </Group>
      )}
      {inForce > 0 && onClearFilters && (
        <Group first={false}>
          <button
            type="button"
            className={cn(rowClass, "text-muted-foreground")}
            onClick={onClearFilters}
          >
            <XIcon aria-hidden className="size-4 shrink-0" />
            Clear filters
          </button>
        </Group>
      )}
    </>
  )
}

/** The check that marks a chosen row, in a slot of its own so every label shares an edge. */
function Check({ checked }: { checked: boolean }) {
  return (
    <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
      {checked && <CheckIcon className="size-4 text-foreground" />}
    </span>
  )
}

/**
 * A facet's values, each with its glyph, its secondary words and how many rows have it, the chosen
 * ones checked; a search over them once there are more than seven, focused only when tapped on
 * touch, so the keyboard doesn't cover the list as it opens.
 */
function FacetValueRows({ facet, onChosen }: { facet: Facet; onChosen: () => void }) {
  const { touch } = useViewport()
  const [query, setQuery] = useState("")
  const multi = facet.mode === "multi"
  const shown = matching(facet.values, query)
  const toggle = (id: string) => {
    if (!multi) {
      facet.onChange(facet.chosen.includes(id) ? [] : [id])
      onChosen()
      return
    }
    facet.onChange(
      facet.chosen.includes(id)
        ? facet.chosen.filter((each) => each !== id)
        : [...facet.chosen, id],
    )
  }
  return (
    <>
      {isSearchable(facet.values) && (
        <SearchRow
          autoFocus={!touch}
          aria-label={`Search ${facet.label}`}
          placeholder={`${facet.label}…`}
          enterKeyHint="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}
      <div role="menu" aria-label={facet.label} className="py-1">
        {shown.length === 0 ? (
          <p className="py-6 text-center text-ui text-subtle-foreground">No matches</p>
        ) : (
          shown.map((value) => (
            <ValueRow
              key={value.id}
              value={value}
              role={multi ? "menuitemcheckbox" : "menuitemradio"}
              checked={facet.chosen.includes(value.id)}
              onClick={() => toggle(value.id)}
            />
          ))
        )}
      </div>
    </>
  )
}

function ValueRow({
  value,
  role,
  checked,
  onClick,
}: {
  value: Pick<FacetOption, "label" | "glyph" | "secondary" | "count">
  role: "menuitemcheckbox" | "menuitemradio"
  checked: boolean
  onClick: () => void
}) {
  return (
    <button type="button" role={role} aria-checked={checked} className={rowClass} onClick={onClick}>
      {value.glyph && <span className="flex shrink-0 items-center">{value.glyph}</span>}
      <span className="min-w-0 flex-1 truncate">
        {value.label}
        {value.secondary && (
          <span className="ml-1.5 text-subtle-foreground">{value.secondary}</span>
        )}
      </span>
      {value.count !== undefined && (
        <span className="pl-2 text-xs text-subtle-foreground tabular-nums">{value.count}</span>
      )}
      <Check checked={checked} />
    </button>
  )
}

/** The choices of an order or a Display option, the current one checked. */
function ChoiceRows({
  label,
  value,
  options,
  onChoose,
}: {
  label: string
  value: string
  options: readonly { value: string; label: string }[]
  onChoose: (value: string) => void
}) {
  return (
    <div role="menu" aria-label={label} className="py-1">
      {options.map((option) => (
        <ValueRow
          key={option.value}
          value={option}
          role="menuitemradio"
          checked={option.value === value}
          onClick={() => onChoose(option.value)}
        />
      ))}
    </div>
  )
}
